"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { IconSearch, type Icon } from "@tabler/icons-react"

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

export type NavSearchEntry = {
  /** Section heading in the palette; entries with the same group cluster together. */
  group?: string
  title: string
  url: string
  icon?: Icon
}

/** Sidebar search — the "Search or jump to…" trigger plus its ⌘K command
 *  palette. Entries come pre-filtered from AppSidebar (same permission/flag
 *  gates as the rail itself), so the palette can never deep-link a user into
 *  a page their nav wouldn't show. */
export function NavSearch({ entries }: { entries: NavSearchEntry[] }) {
  const t = useTranslations("nav")
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  // Rendered after mount only (default matches SSR markup) so the shortcut
  // hint can be platform-aware without a hydration mismatch.
  const [shortcutLabel, setShortcutLabel] = React.useState("")

  React.useEffect(() => {
    const isMac = /Mac|iPhone|iPad/.test(navigator.platform)
    setShortcutLabel(isMac ? "⌘K" : "Ctrl K")
    const down = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  // Cluster consecutive same-group entries (order is the rail's own order),
  // deduping repeated destinations (pinned items also live in their section).
  const groups = React.useMemo(() => {
    const seen = new Set<string>()
    const out: { group?: string; entries: NavSearchEntry[] }[] = []
    for (const entry of entries) {
      if (seen.has(entry.url + "|" + entry.title)) continue
      seen.add(entry.url + "|" + entry.title)
      const last = out[out.length - 1]
      if (last && last.group === entry.group) last.entries.push(entry)
      else out.push({ group: entry.group, entries: [entry] })
    }
    return out
  }, [entries])

  const go = (url: string) => {
    setOpen(false)
    router.push(url)
  }

  return (
    <>
      {/* Expanded rail: full search field. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="border-border bg-card text-text-dim hover:border-border-strong flex w-full items-center gap-2 rounded-[10px] border px-2.5 py-[7px] text-left text-[12.5px] transition-colors group-data-[collapsible=icon]:hidden"
      >
        <IconSearch className="size-3.5 shrink-0" />
        <span className="truncate">{t("searchPlaceholder")}</span>
        {shortcutLabel && (
          <kbd className="bg-muted text-muted-foreground border-border ml-auto rounded-[5px] border px-[5px] font-mono text-[10px] leading-4 whitespace-nowrap">
            {shortcutLabel}
          </kbd>
        )}
      </button>
      {/* Icon-collapsed rail: bare magnifier. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("searchPlaceholder")}
        className="text-text-dim hover:bg-surface-3 hover:text-sidebar-foreground hidden size-8 place-items-center self-center rounded-lg transition-colors group-data-[collapsible=icon]:grid"
      >
        <IconSearch className="size-4" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("searchPlaceholder")} />
        <CommandList>
          <CommandEmpty>{t("searchEmpty")}</CommandEmpty>
          {groups.map((g, idx) => (
            <CommandGroup key={g.group ?? `nav-search-${idx}`} heading={g.group}>
              {g.entries.map((entry) => (
                <CommandItem
                  key={entry.url + entry.title}
                  value={`${entry.group ?? ""} ${entry.title}`}
                  onSelect={() => go(entry.url)}
                >
                  {entry.icon && <entry.icon className="text-text-dim" />}
                  <span>{entry.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  )
}
